import React, { useEffect, useState } from 'react';
import { Card, Typography, Spin, Empty, DatePicker, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs, { Dayjs } from 'dayjs';
import { useSelector } from 'react-redux';
import tenderDashboardApis from '../../api/tenderDashboard/tenderDashboardApis';

const { Title, Text } = Typography;

type MonthlyData = {
  month: string;
  totalTenders: number;
  avgBidders: number;
  tenderValue: number;
};

const MONTH_ORDER = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MarketEngagementTrend: React.FC = () => {
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgId = currentUser?.orgid;

  const [selectedYear, setSelectedYear] = useState<Dayjs>(dayjs());
  const [data, setData] = useState<MonthlyData[] | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async (year: Dayjs) => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await tenderDashboardApis.getMarketEngagementTrend({
        orgIds: String(orgId),
        year: year.year(),
      });
      const rows: any[] = res?.data?.data || [];
      const mapped: MonthlyData[] = rows.map((r) => ({
        month: r?.monthName ?? '',
        totalTenders: r?.totalTenders ?? 0,
        avgBidders: r?.avgBidders ?? 0,
        tenderValue: r?.tenderValue ?? 0,
      }));
      mapped.sort(
        (a, b) => MONTH_ORDER.indexOf(a.month) - MONTH_ORDER.indexOf(b.month)
      );
      setData(mapped);
    } catch (e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, selectedYear]);

  const handleRefresh = () => {
    fetchData(selectedYear);
  };

  const months = data?.map(d => d.month) || [];
  const totalTenders = data?.map(d => d.totalTenders) || [];
  const avgBidders = data?.map(d => d.avgBidders) || [];
  const tenderValues = data?.map(d => d.tenderValue) || [];

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const bars = params
          .filter((p: any) => p.seriesType === 'bar')
          .map((p: any) => `${p.marker} ${p.seriesName}: ${p.value}`);
        const line = params.find((p: any) => p.seriesType === 'line');
        const lineStr = line ? `<br/>📈 ${line.seriesName}: ₹ ${line.value.toLocaleString("en-IN")}` : '';
        return `${params[0].axisValueLabel}<br/>${bars.join('<br/>')}${lineStr}`;
      }
    },
    legend: {
      data: ['Total Tenders', 'Avg. Bidders', 'Tender Value'],
      top: '5%',
      textStyle: { color: '#000' }
    },
    grid: {
      left: '2%',
      right: '4%',
      bottom: '10%',
      top: '18%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: months,
      axisLabel: { color: '#000', fontSize: 12 },
      axisLine: { lineStyle: { color: '#ccc' } }
    },
    yAxis: [
      {
        type: 'value',
        name: 'Count',
        position: 'left',
        axisLine: { lineStyle: { color: '#000' } },
        axisLabel: { color: '#000', fontSize: 12 }
      },
      {
        type: 'value',
        name: 'Tender Value (INR)',
        position: 'right',
        axisLine: { lineStyle: { color: '#000' } },
        axisLabel: { color: '#000', fontSize: 12 },
        splitLine: { show: false }
      }
    ],
    series: [
      {
        name: 'Total Tenders',
        type: 'bar',
        data: totalTenders,
        yAxisIndex: 0,
        itemStyle: { color: '#3762e4', borderRadius: [4, 4, 0, 0] },
        barGap: '20%',
        barWidth: 18
      },
      {
        name: 'Avg. Bidders',
        type: 'bar',
        data: avgBidders,
        yAxisIndex: 0,
        itemStyle: { color: '#6ac5ff', borderRadius: [4, 4, 0, 0] },
        barGap: '0%',
        barWidth: 20
      },
      {
        name: 'Tender Value',
        type: 'line',
        data: tenderValues,
        yAxisIndex: 1,
        lineStyle: { color: '#00d88c', width: 2 },
        symbol: 'circle',
        symbolSize: 10,
        itemStyle: { color: '#00d88c' }
      }
    ]
  };

  return (
    <Card
      title={
        <div style={{ padding: '10px 0' }}>
          <Title level={5} style={{ marginBottom: 0, color: '#004293' }}>
            Market Engagement Trend
          </Title>
          <Text type="secondary" style={{ fontSize: 12, color: '#8094ae' }}>
            Participation volume and tender value month over month
          </Text>
        </div>
      }
      extra={
        <Space>
          <DatePicker
            size="small"
            picker="year"
            value={selectedYear}
            onChange={(d) => d && setSelectedYear(d)}
            allowClear={false}
            style={{ width: 110 }}
          />
          <ReloadOutlined onClick={handleRefresh} style={{ cursor: 'pointer' }} />
        </Space>
      }
      style={{ width: '100%' }}
      bodyStyle={{ padding: '0 14px 12px 14px' }}
    >
      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <Spin tip="Loading chart..." />
        </div>
      ) : !data || data.length === 0 ? (
        <Empty description="No engagement data found" />
      ) : (
        <ReactECharts option={option} style={{ height: 360 }} />
      )}
    </Card>
  );
};

export default MarketEngagementTrend;
